/*
 * Alloy: Personalize It box (Personalized by Alloy, 2026-10-17). DEV theme first; snippets/alloy-personalize.liquid renders it.
 *
 * Reads the listing's rules (product metafield alloy.personalize, written by the Alloy app: sides, our lettering styles and the
 * letter limit for each, per choice), shows Where (Front / Back / Both, or Top / Inside for rings), the words per side with a live
 * counter and preview, and the lettering. A choice with three or more sides (2026-10-18: the four-sided bar, Front / Back /
 * Left Side / Right Side) shows "Which sides?" instead: tick any sides, or "All four sides"; each gets its own words, counter,
 * lettering and preview. It saves line-item properties on the product form:
 *   "Front Engraving", "Front Lettering" (per side, e.g. "Left Side Engraving"; what the customer sees in the cart and at checkout)
 *   and "_pz" (the app's exact copy).
 * Add to Cart waits until every chosen side has words that fit, and (unless the page has its own final-sale tick) the
 * "I checked my spelling" tick. No color fill. The same rules are checked again by staff the same day (php/personalize_lib.php).
 *
 * The pure functions at the top are shared with the node test (D:/alloy_tmp/personalize/theme_test.js).
 */
(function (root, factory) {
  var PZ = factory();
  if (typeof module === 'object' && module.exports) module.exports = PZ;
  else root.AlloyPZ = PZ;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* key -> label + preview font. Same keys as alloy_pz_lettering() in the app. The preview is a close match, not the engraving. */
  var LETTERING = {
    script: { label: 'Script', css: "'Pinyon Script', 'Snell Roundhand', cursive" },
    serif: { label: 'Classic', css: "'Libre Baskerville', Georgia, serif" },
    sans: { label: 'Modern', css: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
    typewriter: { label: 'Typewriter', css: "'Courier Prime', 'Courier New', monospace" },
    oldenglish: { label: 'Old English', css: "'UnifrakturMaguntia', 'Old English Text MT', serif" },
    handwriting: { label: 'Handwritten', css: "'Dancing Script', cursive" },
    edwardian: { label: 'Formal Script', css: "'Italianno', cursive" },
    calligraphy: { label: 'Calligraphy', css: "'Corinthia', cursive" },
    deco: { label: 'Art Deco', css: "'Poiret One', sans-serif" },
    monogram: { label: 'Classic Monogram', css: "'Libre Baskerville', Georgia, serif", mono: true },
    monogram_vine: { label: 'Vine Monogram', css: "'Pinyon Script', cursive", mono: true },
    monogram_circle: { label: 'Circle Monogram', css: "'Libre Baskerville', Georgia, serif", mono: true }
  };
  var GOOGLE = 'https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Libre+Baskerville&family=Courier+Prime&family=UnifrakturMaguntia&family=Dancing+Script&family=Italianno&family=Corinthia&family=Poiret+One&display=swap';
  var CHARS = /^[A-Za-z0-9 .,'&!?#:\/+()-]*$/;
  /* the side order; the same list as ALLOY_PZ_SIDE_ORDER in the app. Any other side name sorts last, in the app's order. */
  var ORDER = ['Front', 'Back', 'Top', 'Inside', 'Outside', 'Left Side', 'Right Side', 'Left', 'Right', 'Bottom',
    'Side 1', 'Side 2', 'Side 3', 'Side 4', 'Side A', 'Side B', 'Side C', 'Side D'];
  var NUM = ['', 'one', 'two', 'three', 'four', 'five', 'six'];

  /* curly quotes and dashes typed on a phone -> the plain ones we can engrave */
  function clean(t) {
    return String(t == null ? '' : t).replace(/[\u2018\u2019\u02BC]/g, "'").replace(/[\u2013\u2014]/g, '-').replace(/\u00A0/g, ' ');
  }

  /* the side map for one choice: rules.variants[sku] -> group, else rules.default */
  function groupFor(rules, sku) {
    if (!rules || !rules.groups) return null;
    var g = (rules.variants && sku != null && rules.variants[sku]) || rules['default'];
    return (g && rules.groups[g]) || null;
  }

  function sidesOf(group) {
    var keys = Object.keys(group || {}), rank = function (s) { var i = ORDER.indexOf(s); return i < 0 ? 100 + keys.indexOf(s) : i; };
    return keys.slice().sort(function (a, b) { return rank(a) - rank(b); });
  }

  /* three or more sides (a four-sided bar): pick any of them ("Which sides?") instead of one-or-both */
  function multi(group) { return sidesOf(group).length >= 3; }

  /* the sides picked on a 3+ side choice, in side order; never none (then the first side) */
  function picked(group, pick) {
    var s = sidesOf(group), out = s.filter(function (x) { return (pick || []).indexOf(x) >= 0; });
    return out.length ? out : s.slice(0, 1);
  }

  /* "All four sides" */
  function allLabel(n) { return 'All ' + (NUM[n] || n) + ' sides'; }

  /* a side as a safe id piece ("Left Side" -> "LeftSide"; Front stays Front) */
  function slug(side) { return String(side).replace(/[^A-Za-z0-9]/g, ''); }

  /* the Where choices: one side -> just it; two -> both names + "Both"; more -> each, plus "All" */
  function whereOptions(group) {
    var s = sidesOf(group);
    if (s.length < 2) return s.map(function (x) { return { value: x, label: x, sides: [x] }; });
    var out = s.map(function (x) { return { value: x, label: x, sides: [x] }; });
    out.push({ value: '*', label: s.length === 2 ? 'Both' : 'All', sides: s.slice() });
    return out;
  }

  /* lettering offered on one side, in our order: [{key, label, max, mono}] */
  function fontsOf(group, side) {
    var f = (group && group[side]) || {};
    return Object.keys(LETTERING).filter(function (k) { return f[k] > 0; }).map(function (k) {
      return { key: k, label: LETTERING[k].label, max: f[k], mono: !!LETTERING[k].mono };
    });
  }

  function maxFor(group, side, font) {
    return (group && group[side] && group[side][font]) || 0;
  }

  /*
   * Check what the customer typed. entries: {Front: {t: 'Emma', f: 'script'}}; chosen: the sides picked in Where.
   * -> {ok, errors: {side: 'message'}, first: side|null}
   */
  function check(group, chosen, entries, required) {
    var errors = {}, first = null, any = false;
    (chosen || []).forEach(function (side) {
      var e = (entries && entries[side]) || {}, t = clean(e.t), f = e.f, max = maxFor(group, side, f), msg = '';
      if (!group || !group[side]) msg = 'This side cannot be engraved on this choice.';
      else if (t.trim() === '') msg = 'Add your words for the ' + side.toLowerCase() + '.';
      else if (!max) msg = 'Choose a lettering style.';
      else if (t.length > max) msg = 'Up to ' + max + (max === 1 ? ' letter' : ' letters') + ' in ' + LETTERING[f].label + '. Please shorten it.';
      else if (!CHARS.test(t)) msg = 'Letters, numbers and simple punctuation only.';
      else if (LETTERING[f].mono && !/^[A-Za-z]+$/.test(t)) msg = 'A monogram takes letters only.';
      if (t.trim() !== '') any = true;
      if (msg) { errors[side] = msg; if (!first) first = side; }
    });
    if (required && !any && !first) { first = (chosen && chosen[0]) || null; if (first) errors[first] = 'Add your words for the ' + first.toLowerCase() + '.'; }
    return { ok: !first, errors: errors, first: first };
  }

  /* the line-item properties for the chosen sides */
  function props(chosen, entries, key) {
    var out = {}, copy = { v: 1, k: key || '', s: {} };
    (chosen || []).forEach(function (side) {
      var e = entries[side] || {}, t = clean(e.t);
      if (t.trim() === '') return;
      if (LETTERING[e.f] && LETTERING[e.f].mono) t = t.toUpperCase();
      out[side + ' Engraving'] = t;
      out[side + ' Lettering'] = LETTERING[e.f] ? LETTERING[e.f].label : e.f;
      copy.s[side] = { t: t, f: e.f };
    });
    out._pz = JSON.stringify(copy);
    return out;
  }

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  /* ------------------------------------------------------------------ the box (browser only) */
  function mount(el) {
    var rules, vmap;
    try { rules = JSON.parse(el.querySelector('[data-pz-rules]').textContent); vmap = JSON.parse(el.querySelector('[data-pz-variants]').textContent); }
    catch (e) { return; }
    if (!rules || !rules.groups) return;
    if (rules.mode === 'checkout') return;   /* a server checkout's rules (the piece configurator's own box, 2026-10-26): never a cart box */
    var sec = el.closest('.shopify-section') || document;
    var form = document.getElementById(el.getAttribute('data-form-id')) || sec.querySelector('form[data-type="add-to-cart-form"]') || sec.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var ownConfirm = !!sec.querySelector('[data-mto-confirm]');      // the ring page's own made-to-order / final-sale tick
    var state = { where: null, pick: null, entries: {}, tried: false };
    var group = null;

    if (!document.getElementById('alloy-pz-fonts')) {
      var l = document.createElement('link'); l.id = 'alloy-pz-fonts'; l.rel = 'stylesheet'; l.href = GOOGLE; document.head.appendChild(l);
    }

    function variantId() { var i = form.querySelector('[name="id"]'); return i ? String(i.value) : ''; }
    function chosen() {
      if (multi(group)) return picked(group, state.pick);
      var opts = whereOptions(group), w = opts.filter(function (o) { return o.value === state.where; })[0] || opts[0];
      return w ? w.sides : [];
    }

    function render() {
      group = groupFor(rules, vmap[variantId()]);
      if (!group) { el.innerHTML = '<p class="alloy-pz__off">This choice cannot be personalized. Please choose another.</p>'; sync(); return; }
      var opts = whereOptions(group);
      if (!opts.some(function (o) { return o.value === state.where; })) state.where = opts[0].value;
      var html = '<div class="alloy-pz__head"><h4 class="alloy-pz__title">Personalize It</h4><span class="alloy-pz__price">'
        + (rules.mode === 'addon' ? '+$' + Number(rules.fee).toFixed(0) : 'Included') + '</span></div>';
      if (multi(group)) {
        /* 3+ sides: pick any (each side gets its own words and lettering), or all of them; one price either way */
        var all = sidesOf(group), on = picked(group, state.pick), every = on.length === all.length;
        state.pick = on;
        html += '<div class="alloy-pz__label" id="pz-which-' + esc(el.id) + '">Which sides?</div><div class="alloy-pz__chips" role="group" aria-labelledby="pz-which-' + esc(el.id) + '">'
          + all.map(function (x) {
            var is = on.indexOf(x) >= 0;
            return '<button type="button" class="alloy-pz__chip alloy-pz__pick' + (is ? ' is-on' : '') + '" role="checkbox" aria-checked="' + is + '" data-pick="' + esc(x) + '">'
              + '<span class="alloy-pz__tick" aria-hidden="true">' + (is ? '\u2713' : '') + '</span>' + esc(x) + '</button>';
          }).join('')
          + '<button type="button" class="alloy-pz__chip alloy-pz__all' + (every ? ' is-on' : '') + '" aria-pressed="' + every + '" data-pick-all>' + esc(allLabel(all.length)) + '</button></div>'
          + '<p class="alloy-pz__hint">One price for one side or all ' + esc(NUM[all.length] || String(all.length)) + '.</p>';
      } else if (opts.length > 1) {
        html += '<div class="alloy-pz__label">Where</div><div class="alloy-pz__chips" role="radiogroup">' + opts.map(function (o) {
          return '<button type="button" class="alloy-pz__chip' + (o.value === state.where ? ' is-on' : '') + '" role="radio" aria-checked="' + (o.value === state.where) + '" data-where="' + esc(o.value) + '">' + esc(o.label) + '</button>';
        }).join('') + '</div>';
      }
      chosen().forEach(function (side) {
        var fonts = fontsOf(group, side), e = state.entries[side] || (state.entries[side] = { t: '', f: '' });
        if (!fonts.some(function (f) { return f.key === e.f; })) e.f = fonts.length ? fonts[0].key : '';
        var max = maxFor(group, side, e.f), mono = LETTERING[e.f] && LETTERING[e.f].mono, sid = slug(side);
        html += '<div class="alloy-pz__side" data-side="' + esc(side) + '">'
          + '<label class="alloy-pz__label" for="pz-' + esc(sid) + '-' + esc(el.id) + '">' + esc(side) + (mono ? ' monogram' : '') + '</label>'
          + '<div class="alloy-pz__field"><input id="pz-' + esc(sid) + '-' + esc(el.id) + '" class="alloy-pz__input" type="text" autocomplete="off" autocapitalize="' + (mono ? 'characters' : 'sentences') + '" spellcheck="true"'
          + ' maxlength="' + max + '" value="' + esc(e.t) + '" placeholder="' + (max <= 3 ? (max === 1 ? 'A' : 'ABC') : 'Your words') + '" data-text>'
          + '<span class="alloy-pz__count" data-count>' + clean(e.t).length + '/' + max + '</span></div>'
          + (fonts.length > 1 ? '<div class="alloy-pz__fonts">' + fonts.map(function (f) {
            return '<button type="button" class="alloy-pz__font' + (f.key === e.f ? ' is-on' : '') + '" data-font="' + esc(f.key) + '" style="font-family:' + esc(LETTERING[f.key].css) + '">' + esc(f.label) + '</button>';
          }).join('') + '</div>' : '')
          + '<div class="alloy-pz__preview" aria-hidden="true" style="font-family:' + esc((LETTERING[e.f] || {}).css || 'inherit') + '" data-preview>' + esc(previewText(e)) + '</div>'
          + '<p class="alloy-pz__err" data-err></p></div>';
      });
      html += '<p class="alloy-pz__note">Personalized pieces are final sale. Please check your spelling.</p>';
      if (!ownConfirm) {
        html += '<label class="alloy-pz__ok"><input type="checkbox" data-spell' + (state.spell ? ' checked' : '') + '> I checked my spelling.</label>';
      }
      html += '<p class="alloy-pz__err" data-err-all></p>';
      el.innerHTML = html;
      showErrors();
      sync();
    }

    function previewText(e) {
      var t = clean(e.t);
      if (!t.trim()) return LETTERING[e.f] && LETTERING[e.f].mono ? 'ABC' : 'Your words';
      return LETTERING[e.f] && LETTERING[e.f].mono ? t.toUpperCase() : t;
    }

    function result() { return check(group, chosen(), state.entries, rules.required !== false); }

    function showErrors() {
      if (!state.tried) return;
      var r = result();
      el.querySelectorAll('[data-side]').forEach(function (s) { var p = s.querySelector('[data-err]'); if (p) p.textContent = r.errors[s.getAttribute('data-side')] || ''; });
      var all = el.querySelector('[data-err-all]');
      if (all) all.textContent = !ownConfirm && r.ok && !state.spell ? 'Please tick that you checked your spelling.' : '';
    }

    /* the hidden inputs on the product form: what the cart line carries */
    function sync() {
      form.querySelectorAll('input[data-pz-prop]').forEach(function (i) { i.remove(); });
      if (!group) return;
      var p = props(chosen(), state.entries, rules.k);
      Object.keys(p).forEach(function (k) {
        var i = document.createElement('input'); i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = p[k]; i.setAttribute('data-pz-prop', ''); form.appendChild(i);
      });
    }

    function ready() { var r = group ? result() : { ok: false }; return r.ok && (ownConfirm || !!state.spell); }

    function block(ev) {
      state.tried = true;
      if (ready()) return false;
      ev.preventDefault(); ev.stopImmediatePropagation();
      showErrors();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var bad = el.querySelector('[data-err]:not(:empty)');
      var input = bad && bad.parentNode.querySelector('[data-text]');
      if (input) input.focus({ preventScroll: true });
      return true;
    }

    el.addEventListener('click', function (ev) {
      var w = ev.target.closest('[data-where]'), f = ev.target.closest('[data-font]'), pk = ev.target.closest('[data-pick]'), pa = ev.target.closest('[data-pick-all]');
      if (w) { state.where = w.getAttribute('data-where'); render(); }
      if (pk || pa) {
        var all = sidesOf(group), cur = picked(group, state.pick), x = pk ? pk.getAttribute('data-pick') : '';
        if (pa) state.pick = cur.length === all.length ? all.slice(0, 1) : all.slice();
        else if (cur.indexOf(x) < 0) state.pick = cur.concat([x]);
        else if (cur.length > 1) state.pick = cur.filter(function (y) { return y !== x; });
        render();
        var back = pa ? el.querySelector('[data-pick-all]') : [].filter.call(el.querySelectorAll('[data-pick]'), function (b) { return b.getAttribute('data-pick') === x; })[0];
        if (back) back.focus();
      }
      if (f) { var side = f.closest('[data-side]').getAttribute('data-side'); state.entries[side].f = f.getAttribute('data-font'); render(); var i = el.querySelector('[data-side="' + side + '"] [data-text]'); if (i) i.focus(); }
    });
    el.addEventListener('input', function (ev) {
      var i = ev.target.closest('[data-text]');
      if (!i) return;
      var side = i.closest('[data-side]').getAttribute('data-side'), e = state.entries[side];
      var v = clean(i.value);
      if (LETTERING[e.f] && LETTERING[e.f].mono) v = v.toUpperCase().replace(/[^A-Z]/g, '');
      if (v !== i.value) i.value = v;
      e.t = v;
      var box = i.closest('[data-side]');
      box.querySelector('[data-count]').textContent = v.length + '/' + maxFor(group, side, e.f);
      box.querySelector('[data-preview]').textContent = previewText(e);
      showErrors(); sync();
    });
    el.addEventListener('change', function (ev) { if (ev.target.matches('[data-spell]')) { state.spell = ev.target.checked; showErrors(); } });

    /* Add to Cart (form submit), the sticky cart bar and the ring page's own button all go through the form: hold them until ready */
    window.addEventListener('submit', function (ev) { if (ev.target === form) block(ev); }, true);
    document.addEventListener('click', function (ev) {
      var b = ev.target.closest('[data-cart-bar-add-to-cart], [data-mto-add]');
      if (b && (b.closest('.shopify-section') === sec || b.hasAttribute('data-cart-bar-add-to-cart'))) block(ev);
    }, true);

    /* the add-on mode: the piece and the Personalization line go into the cart together */
    if (rules.mode === 'addon' && rules.addon) {
      window.addEventListener('submit', function (ev) {
        if (ev.target !== form || ev.defaultPrevented || !ready()) return;
        ev.preventDefault(); ev.stopImmediatePropagation();
        var p = props(chosen(), state.entries, rules.k), ref = 'pz-' + Date.now();
        p._pz_ref = ref;
        fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [
          { id: Number(variantId()), quantity: 1, properties: p },
          { id: Number(rules.addon), quantity: 1, properties: { _pz_for: ref } }] }) })
          .then(function (r) { if (!r.ok) throw new Error('cart'); window.location.href = '/cart'; })
          .catch(function () { var a = el.querySelector('[data-err-all]'); if (a) a.textContent = 'Something went wrong. Please try again.'; });
      }, true);
    }

    /* a different metal or size: new limits */
    var last = variantId();
    function changed() { var v = variantId(); if (v !== last) { last = v; render(); } }
    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function () { setTimeout(changed, 0); });
    sec.addEventListener('change', function () { setTimeout(changed, 50); });
    setInterval(changed, 800);

    render();
  }

  function boot() { document.querySelectorAll('[data-alloy-pz]:not([data-pz-on])').forEach(function (el) { el.setAttribute('data-pz-on', ''); mount(el); }); }
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  }

  return { LETTERING: LETTERING, CHARS: CHARS, ORDER: ORDER, clean: clean, groupFor: groupFor, sidesOf: sidesOf, whereOptions: whereOptions, multi: multi, picked: picked,
    allLabel: allLabel, slug: slug, fontsOf: fontsOf, maxFor: maxFor, check: check, props: props, mount: mount };
});
